/**
 * STEPS demo screens hosted on GitHub Pages.
 *
 * Default: `https://exxatdesign.github.io/prism-demo-screens/...`
 *
 * Local override: set `VITE_PRISM_DEMO_SCREENS_ORIGIN` (e.g. `http://127.0.0.1:5000`
 * when Vite serves `Demo_screens/dist` via `scripts/vite-serve-prism-demo-screens.mjs`).
 */

import type { StepsEmbedNavMode } from "@/hooks/use-steps-embed-nav-mode"
import { getStepsEmbedNavMode } from "@/hooks/use-steps-embed-nav-mode"
import type { PrismEmbedScope } from "@/lib/prism-embed-scope"
import { prismEmbedScopeQuery } from "@/lib/prism-embed-scope"

const DEMO_MOUNT = "/prism-demo-screens"
const DEFAULT_DEMO_ORIGIN = "https://exxatdesign.github.io"

export type PrismDemoScreenId =
  | "clinical-dashboard"
  | "faculty-dashboard"
  | "student-dashboard"
  | "student-compliance-dashboard"
  | "compliance-home-cohort"
  | "faculty-compliance-caas"
  | "caas-faculty-by-group"
  | "curriculum-course-offerings"
  | "profile-search"
  | "program-program-details"
  | "competency-review"
  | "cmap-curriculum"
  | "admin-tenant-apps"
  | "admin-faculty-search"
  | "one-school-dashboard"

const DEMO_SCREEN_PATHS: Record<PrismDemoScreenId, string> = {
  "clinical-dashboard": "/dashboard",
  "faculty-dashboard": "/faculty/dashboard",
  "student-dashboard": "/student",
  "student-compliance-dashboard": "/student/compliance",
  "compliance-home-cohort": "/compliance/home/cohort",
  "faculty-compliance-caas": "/faculty/caas",
  "caas-faculty-by-group": "/caas/faculty/home/bygroup",
  "curriculum-course-offerings": "/curriculum/courseofferings",
  "profile-search": "/profile/search",
  "program-program-details": "/program/programdetails",
  "competency-review": "/competency/review",
  "cmap-curriculum": "/cmap/curriculum",
  "admin-tenant-apps": "/admin/tenant-apps",
  "admin-faculty-search": "/admin/faculty/search",
  "one-school-dashboard": "/one/school/dashboard",
}

/** Always load from GitHub Pages so `embed=1` strips demo gallery chrome. */
const HOSTED_DEMO_SCREENS: ReadonlySet<PrismDemoScreenId> = new Set([
  "admin-tenant-apps",
  "admin-faculty-search",
  "one-school-dashboard",
])

function demoScreensOrigin(screen: PrismDemoScreenId): string {
  const configured = import.meta.env.VITE_PRISM_DEMO_SCREENS_ORIGIN?.trim()
  if (configured) return configured.replace(/\/$/, "")
  if (HOSTED_DEMO_SCREENS.has(screen)) return DEFAULT_DEMO_ORIGIN
  // Vite dev: same-origin proxy serves ../Prism/Demo_screens/dist (latest local build).
  if (
    import.meta.env.DEV &&
    import.meta.env.MODE === "development" &&
    typeof window !== "undefined"
  ) {
    return window.location.origin
  }
  return DEFAULT_DEMO_ORIGIN
}

/** Absolute URL for a built demo screen route. */
export function prismDemoScreenUrl(
  screen: PrismDemoScreenId,
  options?: { embed?: boolean; scope?: PrismEmbedScope; embedNav?: StepsEmbedNavMode },
): string {
  const path = DEMO_SCREEN_PATHS[screen]
  const params = new URLSearchParams()
  if (options?.embed) {
    params.set("embed", "1")
    params.set("embedNav", options.embedNav ?? getStepsEmbedNavMode())
  }
  if (options?.scope) {
    for (const [key, value] of new URLSearchParams(prismEmbedScopeQuery(options.scope))) {
      params.set(key, value)
    }
  }
  const query = params.size > 0 ? `?${params.toString()}` : ""
  return `${demoScreensOrigin(screen)}${DEMO_MOUNT}${path}${query}`
}

/** Default Compliance landing screen in Demo_screens. */
export const COMPLIANCE_DEMO_SCREEN: PrismDemoScreenId = "compliance-home-cohort"
